/**
 * Builds and throws a plain `Error` enriched with `status` + `detail` from a
 * non-OK fetch `Response`. Pairs with `utils/showApiError` on the consumer side.
 *
 * Used by `/prior-auth-utilities/*` clients that follow the
 * "real HTTP status + `body.detail` on failure" contract.
 *
 * @module throwApiError
 */

/**
 * Extracts user-facing text from a FastAPI error body (`{ detail: ... }`).
 *
 * @param body - Parsed JSON error body.
 * @param fallback - Text when `detail` is missing or unparseable.
 * @return Human-readable error message.
 */
export const formatErrorDetail = (body: unknown, fallback: string): string => {
  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return fallback;
  }

  const { detail } = body as { detail?: unknown };

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (typeof item === "object" && item !== null && "msg" in item) {
          const msg = (item as { msg?: unknown }).msg;
          return typeof msg === "string" ? msg : null;
        }
        return null;
      })
      .filter((msg): msg is string => typeof msg === "string");
    return messages.length > 0 ? messages.join("; ") : fallback;
  }

  if (detail !== undefined && detail !== null) {
    return typeof detail === "object" ? JSON.stringify(detail) : String(detail);
  }

  return fallback;
};

/**
 * Reads `body.detail` from the response (best-effort) and throws an `Error`
 * carrying `status` and `detail` as own properties.
 *
 * @param response - Non-OK fetch response (`response.ok === false`).
 * @throws Always — the function never returns.
 */
/** Body shape shared by sibling-propagation endpoints on partial failure. */
export interface SiblingFailuresBody {
  sibling_failures?: Record<string, string> | null;
}

/**
 * Throws when a 2xx response includes `sibling_failures` (linked orders that failed).
 *
 * @param body - Parsed JSON response body.
 * @param fallback - Message when the failures map is empty.
 * @throws Always when `sibling_failures` is non-null.
 */
export const throwIfSiblingFailures = (
  body: SiblingFailuresBody,
  fallback = "Some linked orders could not be updated.",
): void => {
  const { sibling_failures: siblingFailures } = body;
  if (siblingFailures == null) {
    return;
  }

  const detail =
    Object.entries(siblingFailures)
      .map(([orderId, message]) => `${orderId}: ${message}`)
      .join("; ") || fallback;

  const err = new Error(detail) as Error & {
    status: number;
    detail?: string;
    sibling_failures?: Record<string, string>;
  };
  err.status = 400;
  err.detail = detail;
  err.sibling_failures = siblingFailures;
  throw err;
};

export const throwApiError = async (response: Response): Promise<never> => {
  const body = await response.json().catch(() => ({}));
  const detail = formatErrorDetail(body, response.statusText);
  const err = new Error(detail) as Error & {
    status: number;
    detail?: string;
  };
  err.status = response.status;
  err.detail = detail;
  throw err;
};
